// src/app/services/inactivity.service.ts
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '../shared/toast.service';

const INACTIVITY_MS = 3 * 60 * 60 * 1000; // 3 hours
const WARNING_BEFORE_MS = 5 * 60 * 1000;   // warn 5 min before logout
const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'mousedown',
  'touchstart',
  'scroll',
  'click',
];

@Injectable({ providedIn: 'root' })
export class InactivityService {
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private warnTimer: ReturnType<typeof setTimeout> | null = null;
  private running = false;

  constructor(
    private router: Router,
    private toast: ToastService,
    private zone: NgZone
  ) {}

  /** Call after successful login and on app init when already logged in */
  start(): void {
    if (this.running) return;
    this.running = true;

    // Listen outside Angular's zone so these events don't trigger CD on every move
    this.zone.runOutsideAngular(() => {
      ACTIVITY_EVENTS.forEach((ev) =>
        window.addEventListener(ev, this.onActivity, { passive: true })
      );
    });

    this.resetTimers();
  }

  /** Call on manual logout or when navigating to unauthenticated pages */
  stop(): void {
    if (!this.running) return;
    this.running = false;
    ACTIVITY_EVENTS.forEach((ev) =>
      window.removeEventListener(ev, this.onActivity)
    );
    this.clearTimers();
  }

  private onActivity = (): void => {
    // Reset the idle countdown on any user activity
    this.resetTimers();
  };

  private resetTimers(): void {
    this.clearTimers();

    // Warn 5 minutes before the 3-hour mark
    this.warnTimer = setTimeout(() => {
      this.zone.run(() => {
        this.toast.showError(
          'You will be automatically logged out in 5 minutes due to inactivity.'
        );
      });
    }, INACTIVITY_MS - WARNING_BEFORE_MS);

    // Force logout at exactly 3 hours of no activity
    this.idleTimer = setTimeout(() => {
      this.zone.run(() => {
        this.forceLogout();
      });
    }, INACTIVITY_MS);
  }

  private clearTimers(): void {
    if (this.idleTimer) { clearTimeout(this.idleTimer); this.idleTimer = null; }
    if (this.warnTimer) { clearTimeout(this.warnTimer); this.warnTimer = null; }
  }

  private forceLogout(): void {
    this.stop();
    ['authToken', 'userId', 'email', 'name', 'role', 'packageId'].forEach(
      (k) => localStorage.removeItem(k)
    );
    this.toast.showError(
      'You have been logged out due to 3 hours of inactivity.'
    );
    this.router.navigate(['/login']);
  }
}